// Settings layer — mounted on Chat itself (Chat.Settings), opened by the
// header SettingsBtn + the composer SettingsBtn (`preferences` glyph — the
// same params affordance as the workspace prompt's ParamsBtn). It hosts ONE
// body, the shared `AiSettingsPanel` (workspace/packages/shared/components/
// AiSettingsPanel.js), which is the SAME body the canvas prompt bar's
// ParamsBtn dropdown renders — so the two assistant surfaces are identical
// by construction. AiSettingsPanel owns everything that used to be split
// across this drawer's TargetSection, AiModePickerBody and BridgeField: the
// per-me / whole-workspace scope, the real provider picker (modelMode), the
// local simone-bridge setup, and the authorization mode.
//
// POSITIONING — an IN-SURFACE layer, not a viewport modal. `absolute` +
// Chat root's `position: relative` anchor the scrim + sheet to the assistant
// surface itself: in the workspace drawer the sheet slides over the sidebar
// content right under the header SettingsBtn (previously `fixed; inset: 0`
// centered it mid-viewport, visually detached from its trigger); in the
// full-surface hosts (standalone simone, chrome-connect, ext-ui) Chat fills
// the page so the geometry is unchanged. Contained by construction: the
// sheet can never overflow the viewport edge, and on phones (drawer =
// full-width overlay) it becomes a full-width sheet automatically.
export const SettingsDrawer = {
  position: 'absolute',
  inset: '0',
  background: 'scrim',
  align: 'flex-start center',
  padding: 'A',
  // Above the in-chat layers (Sessions menu 200, composer chips 25) within
  // the assistant surface's stacking context.
  zIndex: '300',
  // The layer is `if:`-created (Chat.Settings), so a brand entrance keyframe
  // plays on every open — opacity-only: this is a sheet, not a dropdown, and
  // a tall surface travelling would overshoot the motion budget.
  animation: 'fadeIn',
  animationDuration: 'A',
  onClick: (e, el, s) => {
    // Scrim click closes; Sheet stops propagation below.
    s.root.update({ showSettings: false })
  },

  Sheet: {
    flow: 'y',
    gap: 'A',
    width: '100%',
    // Token cap for wide hosts (full-page simone); inside the drawer the
    // width: 100% minus the scrim padding governs. The old F2 cap was
    // NARROWER than the panel's own min width — permanent overflow.
    maxWidth: 'G1',
    maxHeight: '100%',
    overflow: 'auto',
    // Don't chain this layer's edge-bounce to the surface behind (iPad P2).
    overscrollBehavior: 'contain',
    padding: 'A',
    // THE AI menu surface (AiMenu.js system) at sheet geometry: the same
    // glass every AI dropdown wears, radiusSheet (docked-sheet class, brand
    // geometry) and NO added shadow — the glass theme carries the single
    // budget shadow. Borderless via longhand (the theme emits longhands).
    theme: 'canvas-card-glass',
    round: 'radiusSheet',
    borderWidth: '0',
    onClick: (e) => e.stopPropagation(),

    Header: {
      flow: 'x',
      align: 'center',
      gap: 'Z',
      Title: {
        tag: 'h2',
        text: '{{ ai.settings.panelLabel | polyglot }}',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        margin: '0',
        flex: '1'
      },
      Close: {
        tag: 'button',
        type: 'button',
        text: '{{ ai.bridge.done | polyglot }}',
        padding: 'Y Z1',
        round: 'radiusControl',
        border: 'none',
        theme: 'tertiary',
        color: 'title',
        cursor: 'pointer',
        fontWeight: '700',
        fontSize: 'Z1',
        ':hover': { background: 'hover' },
        onClick: (e, el, s) => s.root.update({ showSettings: false })
      }
    },

    // The single shared body — provider (real modelMode), local simone-bridge
    // setup, authorization, and the per-me / whole-workspace scope toggle.
    // minWidth 0 so the panel's dropdown-host min width can never force the
    // sheet wider than a narrow (320px-min) drawer.
    AiSettingsPanel: {
      minWidth: '0',
      maxWidth: '100%'
    }
  }
}
